import * as amplitude from "@amplitude/analytics-browser";
import { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { getReturnFlow } from "@redotech/redo-model/agents/returns-agents/return-reasons";
import { SuggestedVariantOptions } from "@redotech/redo-model/agents/returns-agents/suggested-variant-options/suggested-variant-options";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import {
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import {
  getPrimaryProduct,
  walkFlow,
} from "@redotech/redo-model/draft-return/util";
import {
  getResource,
  Resource,
} from "@redotech/redo-model/localization/resource";
import { Channel } from "@redotech/redo-model/return";
import {
  ClaimOption,
  InputAnswerPart,
  InputAnswerPartWithStep,
  MultipleChoiceOption,
  returnFlowJsonFormat,
  ReturnOption,
  Step,
} from "@redotech/redo-model/return-flow";
import { isAiExchangeEnabled } from "@redotech/redo-model/return-flow/ai-exchange-utils";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { assertNever } from "@redotech/util/type";
import { useContext, useMemo, useState } from "react";
import * as z from "zod/v4";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { SuggestedExchangeOptionsContext } from "../../contexts/suggested-exchange-options";
import {
  getExchangeRecommendations,
  getSuggestedVariantExchangeOptions,
} from "../../flow/ai-exchange-agent-requests";
import {
  CompensationMethodData,
  exchangeVariantToVariantData,
  getEligibleExchangeGroupsForProduct,
  getNonExchangeReturnOptions,
  getReturnedProductVariantData,
  getSameItemVariant,
  getSourceNoteInputAnswer,
  getVariantExchangeReturnOption,
  ItemReturnFlowStep,
  NewReturnItemData,
  ReturnOptionType,
  ReturnStepPageOption,
  StepType,
} from "../../flow/util";
import { Navigator, RouteName, StackParamList } from "../../navigator";
import { ReturnAppSettings } from "../../settings";
import {
  removeGidFromId,
  ReturnItemParams,
  returnItemToParams,
} from "../../util";
import {
  fireSuggestedExchangeEvent,
  getSuggestedExchangeResult,
  SuggestedExchangeEventType,
  SuggestedExchangeResult,
} from "../../utils/suggested-exchange-events";
import { RecommendedExchangeProductData } from "../use-recommended-exchange/use-recommended-exchange";
import {
  ExchangeGroupByProductId,
  getExchangeGroupsByProductId,
  SuggestedExchangeGroupOptions,
} from "./use-exchange-groups";

export type VariantData = {
  variantId: string;
  productId?: string | null;
  productTitle?: string | null;
  variantTitle: string;
  price: { amount: string; currency: string };
  compareAtPrice?: { amount: string; currency: string };
  image: { url: string; altText?: string };
  options: { name: string; value: string; productAsVariant?: boolean | null }[];
  onlineInventoryAvailable: number;
  // this can be true even if onlineInventoryAvailable is 0 depending on the merchant's settings
  // (e.g. if continue when out of stock is enabled)
  isInStock: boolean;
  backorder?: string | null;
  sku?: string;
  barcode?: string;
};

const VariantDataZodSchema_ = z.object({
  variantId: z.string(),
  productId: z.string().nullable().optional(),
  productTitle: z.string().nullable().optional(),
  variantTitle: z.string(),
  price: z.object({ amount: z.string(), currency: z.string() }),
  compareAtPrice: z
    .object({ amount: z.string(), currency: z.string() })
    .optional(),
  image: z.object({ url: z.string(), altText: z.string().optional() }),
  options: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      productAsVariant: z.boolean().nullable().optional(),
    }),
  ),
  onlineInventoryAvailable: z.number(),
  isInStock: z.boolean(),
  backorder: z.string().nullable().optional(),
  sku: z.string().optional(),
  barcode: z.string().optional(),
});
export const VariantDataZodSchema: z.ZodType<VariantData> =
  VariantDataZodSchema_;
export interface VariantDataZod extends z.infer<typeof VariantDataZodSchema_> {}

export type ProductVariantData = {
  productId: string;
  productTitle: string;
} & VariantData;

export type ShopOnSiteProductData = {
  onlineStoreUrl?: string;
  handle?: string;
};

export type ProductData = {
  productId: string;
  productTitle: string;
  options: { name: string; values: string[] }[];
  variants: VariantData[];
  shopOnSite?: ShopOnSiteProductData;
};

interface useFlowResponse {
  componentProps: ComponentType;
  error: string | undefined;
  next: (() => void) | undefined;
  nextButtonDisabled: boolean;
  previousButtonDisabled: boolean;
  nextAlt?: (() => void) | undefined;
  nextAltButtonDisabled?: boolean;
  isRejected: boolean;
  itemDetailParams?: ReturnItemParams;
  texts: { continueButton: string; continueWithoutButton?: string | undefined };
}

export interface BlockStepProps {
  step: Step.Block;
}

export interface InformationStepProps {
  step: Step.Information;
}
export interface InputStepProps {
  step: Step.Input;
  item?: ReturnableItem;
  product?: ReturnProduct;
  navigation: Navigator;
  submit: (answerParts: InputAnswerPart[] | undefined) => void;
}

export interface MultipleChoiceOptionWithSourceIndex
  extends MultipleChoiceOption {
  sourceIndex: number;
}

export interface MultipleChoiceStepProps {
  step: Step.MultipleChoice;
  item?: ReturnableItem;
  product?: ReturnProduct;
  draftReturnId: string;
  stepIndex: number;
  onPress: (option: MultipleChoiceOptionWithSourceIndex) => void;
}

export interface RejectStepProps {
  navigation: Navigator;
  step: Step.Reject;
  reject: () => Promise<void>;
  merchantCreatedProps: {
    onChangeResetPoint: (step: number, flow: any) => Promise<void>;
    onResetDraftReturn: (step: number, flow: any) => Promise<void>;
    flow: any;
  };
}

export interface VariantExchangeAddOnStepProps {
  item: ReturnableItem;
  onAccept: () => void;
  onReject: () => void;
  option: ReturnOption | ClaimOption;
  suggestedExchangeResult?: SuggestedExchangeResult;
}

export interface ReturnStepProps {
  item: ReturnableItem;
  product: ReturnProduct;
  settings: ReturnAppSettings;
  step: Step.Return;
  submit: (data: ReturnStepPageOption) => void;
  suggestedExchangeOptionsLoadPending: boolean;
  copyResource?: Resource;
}

export interface SelectExchangeProductStepProps {
  item: ReturnableItem;
  groups: ExchangeGroup[];
  navigation: Navigator;
  selectProduct: (
    selectedProductAndVariant: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => void;
  submitProduct: (
    selectedProductAndVariant: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => void;
  suggestedExchangeOptions?: SuggestedExchangeGroupOptions;
}

export interface VariantExchangeStepProps {
  product: ProductData;
  returnedProductVariantData: VariantData | undefined;
  suggestedVariantExchangeOptions: SuggestedVariantOptions | undefined;
  submit: (variant: VariantData | undefined) => void;
  settings?: ReturnAppSettings;
  item: ReturnableItem;
  disableExchangeValueAsPriceDifference: boolean;
}

export interface SuggestedExchangeGroupsProps {
  groups: ExchangeGroup[];
  navigation: Navigator;
  selectProduct: (
    selectedProductAndVariant: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => void;
  submitProduct: (
    selectedProductAndVariant: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => void;
  showAllExchangeGroups: (
    exchangeGroups: ExchangeGroup[],
    suggestedExchangeOptions?: {
      suggestedVariantOptions?: { name: string; values: string[] }[];
      disableExchangeValueAsPriceDifference: boolean;
    },
  ) => void;
  exchangeRecommendations: RecommendedExchangeProductData[];
}

export interface SuggestedExchangeStepProps {
  item: ReturnableItem;
  product: ProductData;
  returnedProductVariantData: VariantData | undefined;
  settings: ReturnAppSettings;
  suggestedVariantExchangeOptions: SuggestedVariantOptions;
  submit: (variant: VariantData | undefined) => void;
  suggestedExchangeGroupsProps: SuggestedExchangeGroupsProps | undefined;
  disableExchangeValueAsPriceDifference: boolean;
}

export interface RecommendedExchangeStepProps {
  item: ReturnableItem;
  exchangeRecommendations: RecommendedExchangeProductData[];
  navigation: Navigator;
  settings: ReturnAppSettings;
  selectProduct: (
    productAndVariant: ProductVariantData | undefined,
    index: number,
  ) => void;
  submitProduct: (
    productAndVariant: ProductVariantData | undefined,
    index: number,
  ) => void;
  outOfStockSameItemReplacement: boolean;
  groupsByProductId: ExchangeGroupByProductId | undefined;
  aiProductRecommendationLimit?: number;
  suggestedExchangeOptions?: {
    suggestedVariantOptions?: { name: string; values: string[] }[];
    disableExchangeValueAsPriceDifference: boolean;
  };
}

export enum ComponentStepType {
  BLOCK = "block",
  INPUT = "input",
  MULTIPLE_CHOICE = "multiple-choice",
  REJECT = "reject",
  RETURN = "return",
  EXCHANGE_GROUPS = "exchange-groups",
  VARIANT_EXCHANGE = "variant-exchange",
  SUGGESTED_EXCHANGE = "suggested-exchange",
  RECOMMENDED_EXCHANGE = "recommended-exchange",
  INFORMATION = "information",
  FIXED_COMPENSATION = "fixed-compensation",
  SUBMIT_REGISTRATION = "submit-registration",
  VARIANT_EXCHANGE_ADD_ON = "variant-exchange-add-on",
}

export type ComponentType =
  | { type: ComponentStepType.BLOCK; props: BlockStepProps }
  | { type: ComponentStepType.INPUT; props: InputStepProps }
  | { type: ComponentStepType.MULTIPLE_CHOICE; props: MultipleChoiceStepProps }
  | { type: ComponentStepType.REJECT; props: RejectStepProps }
  | { type: ComponentStepType.RETURN; props: ReturnStepProps }
  | {
      type: ComponentStepType.EXCHANGE_GROUPS;
      props: SelectExchangeProductStepProps;
    }
  | {
      type: ComponentStepType.VARIANT_EXCHANGE;
      props: VariantExchangeStepProps;
    }
  | {
      type: ComponentStepType.SUGGESTED_EXCHANGE;
      props: SuggestedExchangeStepProps;
    }
  | {
      type: ComponentStepType.RECOMMENDED_EXCHANGE;
      props: RecommendedExchangeStepProps;
    }
  | { type: ComponentStepType.INFORMATION; props: InformationStepProps }
  | { type: ComponentStepType.FIXED_COMPENSATION }
  | { type: ComponentStepType.SUBMIT_REGISTRATION }
  | {
      type: ComponentStepType.VARIANT_EXCHANGE_ADD_ON;
      props: VariantExchangeAddOnStepProps;
    };

type FlowParams = StackParamList[RouteName.FLOW];

type FixedCompensationStep =
  | Step.SubmitClaim
  | Step.SubmitWarranty
  | Step.SubmitRepair;

export const useFlow = (
  navigation: Navigator,
  params: FlowParams,
  settings: ReturnAppSettings | undefined,
  exitOutOfApp: () => Promise<void>,
  channel: Channel,
  clearDraftReturn: () => Promise<void>,
  isMerchant: boolean = false,
): useFlowResponse => {
  const client = useContext(RpcClientContext);
  const returnContext = useContext(ReturnContext);
  const suggestedExchangeOptionsContext = useContext(
    SuggestedExchangeOptionsContext,
  );

  const [isSubmittingFlow, setIsSubmittingFlow] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [next, setNext] = useState<() => void>();
  const [nextAlt, setNextAlt] = useState<(() => void) | undefined>(undefined);
  const draftReturn = returnContext.draftReturn;

  const itemId = params.itemId;
  const returnItem = returnContext.draftReturn?.returnableItems.find(
    (t) => t.id === itemId,
  );
  const product = returnItem?.products.find(
    (p) => p.id === returnItem?.primaryProductId,
  );
  const order = draftReturn?.orders.find(
    (order) => order.id === returnItem?.orderId,
  );

  const exitFlow = () => {
    //There is a crazy hard bug that is stopping us from being approved when using navigation.navigate("item-details") this might fix it
    navigation.dispatch({
      type: "RESET",
      payload: {
        index: 1,
        routes: [
          { name: RouteName.SELECT_ITEMS, params: undefined },
          { name: RouteName.ITEM_DETAILS, params: undefined },
        ],
      },
    });
  };

  const rejectItem = async (
    draftReturn: DraftReturn,
    customerPortalClient: CustomerPortalRpcClient,
    newReturnItemData: NewReturnItemData,
  ) => {
    // Remove previous pending items so we are only rejecting the current item
    for (const pendingReturnItem of draftReturn.pendingReturnItems) {
      await customerPortalClient.removeReturnItem({
        draftReturnId: draftReturn._id,
        pendingItemId: pendingReturnItem.id,
      });
    }
    await addItemToReturn({ newReturnItemData, isRejected: true });
    await customerPortalClient.submitDraftReturn({
      channel,
      draftReturnId: draftReturn._id,
      isRejected: true,
      aiExchangeRecommendationsEnabled: false,
    });
    await clearDraftReturn();
  };

  const informationStepNext = (option: Step.Information) => {
    if (option.next === undefined) {
      return;
    }
    const nextPath = getNextFlowStep(option.next);

    const newItemReturnFlow: ItemReturnFlowStep[] = [
      ...flowSteps,
      {
        type: StepType.FLOW,
        flowPath: [...(step?.flowPath ?? []), ...nextPath],
        inputAnswers: step?.inputAnswers ?? [],
        multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
      },
    ];
    navigation.push(RouteName.FLOW, {
      itemId,
      itemReturnFlowSteps: newItemReturnFlow,
    });
  };

  const selectMultipleChoice = (
    option: MultipleChoiceOption,
    index: number,
  ) => {
    if (flowStepIndex === undefined) {
      return;
    }
    const multipleChoiceAnswers = [
      ...(step?.multipleChoiceAnswers ?? []),
      { step: flowStepIndex, answer: index },
    ];
    const nextPath = getNextFlowStep(option.next);
    const lastPathIndex = nextPath[nextPath.length - 1];
    if (lastPathIndex === undefined) {
      return;
    }
    const nextStep = flow.steps[lastPathIndex];
    if (!nextStep) {
      return;
    }

    const newItemReturnFlow: ItemReturnFlowStep[] = [
      ...flowSteps,
      {
        type: StepType.FLOW,
        flowPath: [...(step?.flowPath ?? []), ...nextPath],
        inputAnswers: step?.inputAnswers ?? [],
        multipleChoiceAnswers,
      },
    ];
    navigation.push(RouteName.FLOW, {
      itemId,
      itemReturnFlowSteps: newItemReturnFlow,
    });
  };

  const addItemToReturn = async ({
    newReturnItemData,
    isRejected,
    suggestedExchangeResult,
  }: {
    newReturnItemData: NewReturnItemData;
    isRejected: boolean;
    suggestedExchangeResult?: SuggestedExchangeResult;
  }) => {
    if (!draftReturn || !client) {
      return;
    }
    const {
      item,
      flowPath,
      multipleChoiceAnswers,
      inputAnswers,
      variantExchangeItem,
      exchangeGroup,
      advancedExchangeItems,
    } = newReturnItemData;
    try {
      /**
       * If we already have a pending item, carry over its quantity
       * Otherwise, this is brand-new, so we'll use the primary product's quantity
       */
      const quantity = item.pendingItem
        ? (item.pendingItem.quantity ?? 1)
        : (getPrimaryProduct(item)?.quantity ?? item.availableQuantity ?? 1);

      const allInputAnswers: InputAnswerPartWithStep[] = [...inputAnswers];
      const sourceNoteInputAnswer = getSourceNoteInputAnswer(draftReturn);
      if (sourceNoteInputAnswer) {
        allInputAnswers.push(sourceNoteInputAnswer);
      }

      setIsSubmittingFlow(true);
      const request = {
        draftReturnId: draftReturn._id,
        previousPendingItemId: item.pendingItem?.id,
        pendingItem: {
          returnableItemId: item.id,
          flow: returnFlowJsonFormat.write(flow),
          flowPath,
          quantity,
          inputAnswers: allInputAnswers.map((answer) => ({
            type: answer.type,
            value: Array.isArray(answer.value)
              ? answer.value.map((url) => url.toString())
              : answer.value,
            message: answer.message,
            step: answer.step,
          })),
          multipleChoiceAnswers,
          variantExchangeItem,
          exchangeGroup,
          advancedExchangeItems,
          metadata: {
            variantSuggested: suggestedVariantOptions?.shouldSuggest,
            recommendedExchangesCount:
              suggestedExchangeOptionsContext?.exchangeRecommendations.length ??
              0,
            suggestedExchangeResult,
          },
          returnReasons: item.pendingItem?.returnReasons ?? [],
        },
      };

      const { draftReturn: updatedDraftReturn } =
        await client.addReturnItem(request);

      if (!isRejected) {
        returnContext.setDraftReturn(updatedDraftReturn);
        exitFlow();
      }
    } catch (e) {
      console.error(e);
      setError("Something went wrong adding the item to the return");
    } finally {
      setIsSubmittingFlow(false);
      suggestedExchangeOptionsContext.setExchangeGroupsByProductId(undefined);
      suggestedExchangeOptionsContext.setSuggestedVariantOptions(undefined);
    }
  };

  const compensationMethod = async (
    data: CompensationMethodData,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => {
    if (suggestedExchangeResult) {
      fireSuggestedExchangeEvent(suggestedExchangeResult);
    }
    await addItemToReturn({
      newReturnItemData: {
        ...data,
        item: { ...returnItem!, availableQuantity: 1 },
        flow,
        flowPath: step?.flowPath ?? [],
        multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
        inputAnswers: step?.inputAnswers ?? [],
      },
      isRejected: false,
      suggestedExchangeResult,
    });
  };

  const variantDataToVariantExchangeItem = (variant: VariantData) => ({
    variantId: variant.variantId,
    productId: variant.productId,
    productTitle: variant.productTitle,
    preserveProperties: true,
    title: variant.variantTitle,
    image: variant.image.url,
  });

  const getVariantExchangeAddOnProps = (
    variant: VariantData,
    option: ReturnOption | ClaimOption,
    addOnVariantId?: string,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ): VariantExchangeAddOnStepProps => {
    const variantExchangeItem = variantDataToVariantExchangeItem(variant);

    const handleAccept = () => {
      setNext(() => () => {
        void compensationMethod(
          {
            variantExchangeItem,
            advancedExchangeItems: [
              {
                variantId: addOnVariantId ?? "",
                quantity: 1,
                properties: null,
                appliedDiscount: null,
              },
            ],
          },
          suggestedExchangeResult,
        );
      });
    };

    const handleReject = () => {
      setNext(() => () => {
        void compensationMethod(
          { variantExchangeItem },
          suggestedExchangeResult,
        );
      });
    };

    return {
      item: returnItem!,
      onAccept: handleAccept,
      onReject: handleReject,
      option,
    };
  };

  const handleReturnStepOption = async (
    data: ReturnStepPageOption,
    exchangeWasSuggested?: boolean,
  ) => {
    switch (data.type) {
      case ReturnOptionType.SAME_ITEM_EXCHANGE: {
        const variantExchangeItem = {
          variantId: data.variant.id,
          productId: data.variant.productId,
          productTitle: data.variant.productTitle,
          preserveProperties: true,
          image: data.variant.image,
          title: data.variant.title,
        };

        if (data.returnOption?.variantExchangeAddOn && settings && product) {
          // Push add-on step instead of finalizing
          const selectedVariant = exchangeVariantToVariantData(
            data.variant,
            settings,
            product,
            draftReturn?.returnType,
            draftReturn?.currency,
          );
          const newFlow: ItemReturnFlowStep[] = [
            ...flowSteps,
            {
              type: StepType.VARIANT_EXCHANGE_ADD_ON,
              selectedVariant,
              returnOption: data.returnOption,
              flowPath: step?.flowPath ?? [],
              inputAnswers: step?.inputAnswers ?? [],
              multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
            },
          ];
          navigation.push(RouteName.FLOW, {
            itemId,
            itemReturnFlowSteps: newFlow,
          });
        } else {
          // Normal flow - finalize immediately
          await compensationMethod({ variantExchangeItem });
        }
        break;
      }
      case ReturnOptionType.VARIANT_EXCHANGE: {
        const newFlow: ItemReturnFlowStep[] = [
          ...flowSteps,
          {
            type: StepType.VARIANT_EXCHANGE,
            flowPath: step?.flowPath ?? [],
            inputAnswers: step?.inputAnswers ?? [],
            multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
          },
        ];

        navigation.push(RouteName.FLOW, {
          itemId,
          itemReturnFlowSteps: newFlow,
        });

        break;
      }
      case ReturnOptionType.EXCHANGE_GROUPS: {
        const newFlow: ItemReturnFlowStep[] = [
          ...flowSteps,
          {
            type: StepType.EXCHANGE_GROUPS,
            exchangeGroups: data.eligibleExchangeGroups,
            flowPath: step?.flowPath ?? [],
            inputAnswers: step?.inputAnswers ?? [],
            multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
          },
        ];

        navigation.push(RouteName.FLOW, {
          itemId,
          itemReturnFlowSteps: newFlow,
        });
        break;
      }
      case ReturnOptionType.SHOP_OR_RETURN: {
        await compensationMethod({});
        break;
      }
    }
    if (exchangeWasSuggested) {
      amplitude.logEvent("return-option-after-suggested-exchange", {
        type: data.type,
      });
    }
  };

  const handleSubmitInput = useHandler(
    (answerParts: InputAnswerPart[] | undefined) => {
      if (!answerParts) {
        setNext(undefined);
        return;
      }

      setNext(() => () => submitInput(answerParts));
    },
  );
  const submitInput = useHandler((answerParts: InputAnswerPart[]) => {
    if (flowStep?.type !== Step.INPUT) {
      throw new Error("Invalid step type");
    }

    if (flowStepIndex === undefined) {
      return;
    }

    const answerPartsWithStep: InputAnswerPartWithStep[] = answerParts.map(
      (answerPart) => ({ ...answerPart, step: flowStepIndex }),
    );
    const inputAnswers = [
      ...(step?.inputAnswers ?? []),
      ...answerPartsWithStep,
    ];
    const nextPath = getNextFlowStep(flowStep?.next);

    const newItemReturnFlow: ItemReturnFlowStep[] = [
      ...flowSteps,
      {
        type: StepType.FLOW,
        flowPath: [...(step?.flowPath ?? []), ...nextPath],
        inputAnswers,
        multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
      },
    ];

    navigation.push(RouteName.FLOW, {
      itemId,
      itemReturnFlowSteps: newItemReturnFlow,
    });
  });

  const submitFixedCompensation = useHandler((step: FixedCompensationStep) => {
    if (!settings || isSubmittingFlow || error) {
      return;
    }
    let data: CompensationMethodData = {};
    const sameItemVariant = getSameItemVariant(
      product!,
      settings,
      draftReturn?.returnType,
    );
    const type = step.type;
    switch (type) {
      /**
       * Submit claim and submit warranty have three options for resolution
       * 1. Exchange for same item
       * 2. Store credit
       * 3. Refund
       */
      case Step.SUBMIT_CLAIM:
        if (
          step.option.method.type === ReturnOptionMethod.EXCHANGE &&
          sameItemVariant
        ) {
          data = {
            variantExchangeItem: {
              variantId: sameItemVariant.id,
              productId: sameItemVariant.productId,
              productTitle: sameItemVariant.productTitle,
              preserveProperties: true,
              image: sameItemVariant.image,
              title: sameItemVariant.title,
            },
          };
        }
        break;
      case Step.SUBMIT_WARRANTY:
        if (
          step.option.method.type === ReturnOptionMethod.EXCHANGE &&
          sameItemVariant
        ) {
          data = {
            variantExchangeItem: {
              variantId: sameItemVariant.id,
              productId: sameItemVariant.productId,
              productTitle: sameItemVariant.productTitle,
              preserveProperties: true,
              image: sameItemVariant.image,
              title: sameItemVariant.title,
            },
          };
        }
        break;
      /**
       * Repair is a unique type of compensation, as there is no "item value" represented in resolving the customers issue
       * The customer sends in the item, the merchant repairs it and then returns the same item to the customer
       */
      case Step.SUBMIT_REPAIR:
        break;
      default:
        assertNever(type);
    }

    void compensationMethod(data);
  });

  const continueToRecommendedExchange = useHandler(() => {
    const newFlow: ItemReturnFlowStep[] = [
      ...flowSteps,
      {
        type: StepType.RECOMMENDED_EXCHANGE,
        flowPath: step?.flowPath ?? [],
        inputAnswers: step?.inputAnswers ?? [],
        multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
      },
    ];
    navigation.push(RouteName.FLOW, { itemId, itemReturnFlowSteps: newFlow });
  });

  const continueWithoutSuggestedExchange = async (
    shownSuggestedVariants: boolean | undefined,
  ) => {
    const suggestedExchangeResult = getSuggestedExchangeResult({
      type: SuggestedExchangeEventType.NO_EXCHANGE,
    });
    fireSuggestedExchangeEvent(suggestedExchangeResult);

    if (flowStep?.type === Step.RETURN && shownSuggestedVariants) {
      const nonExchangeReturnOptions = getNonExchangeReturnOptions(
        flowStep,
        product,
        settings,
        returnItem,
        draftReturn?.returnType,
      );

      // If there is only one remaining option and it's to shop or return, go directly there,
      // since we have already confirmed that they don't want to exchange (even for the same item).
      if (
        nonExchangeReturnOptions.length === 1 &&
        nonExchangeReturnOptions[0]?.type === ReturnOptionType.SHOP_OR_RETURN
      ) {
        await handleReturnStepOption(nonExchangeReturnOptions[0]);
        return;
      }
    }

    const newFlow: ItemReturnFlowStep[] = [
      ...flowSteps,
      {
        type: StepType.FLOW,
        flowPath: step?.flowPath ?? [],
        inputAnswers: step?.inputAnswers ?? [],
        multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
        // The customer was already been suggested to do a variant exchange don't do it again.
        alreadySuggestedExchange: true,
      },
    ];

    // If there are more return options to pick from then land on the full return options page.
    navigation.push(RouteName.FLOW, { itemId, itemReturnFlowSteps: newFlow });
  };

  const submitProductForRecommendedExchange = (
    selectedProductAndVariant: ProductVariantData | undefined,
    index: number,
  ) => {
    if (!selectedProductAndVariant) {
      setNext(undefined);
      return;
    }

    const suggestedExchangeResult = getSuggestedExchangeResult({
      type: SuggestedExchangeEventType.RECOMMENDED_EXCHANGE,
      rank: index + 1,
    });

    // If we are an exchange group, we need to submit that differently to potentially preserve price differences.
    const groupsByProductId =
      suggestedExchangeOptionsContext.exchangeGroupsByProductId;
    if (groupsByProductId) {
      const group = groupsByProductId.get(
        selectedProductAndVariant.productId,
      )?.group;
      if (group) {
        compensationForGroupExchange(
          selectedProductAndVariant,
          group,
          suggestedExchangeResult,
        );
        return;
      }
    }
    void compensationMethod(
      {
        advancedExchangeItems: [
          {
            variantId: selectedProductAndVariant.variantId,
            productId: selectedProductAndVariant.productId,
            quantity: 1,
            properties: { exchangeRecommendation: "true" },
          },
        ],
      },
      suggestedExchangeResult,
    );
  };

  const compensationForGroupExchange = (
    selectedProductAndVariant: ProductVariantData,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => {
    void compensationMethod(
      {
        variantExchangeItem: {
          variantId: removeGidFromId(selectedProductAndVariant.variantId),
          preserveProperties: false,
          title: selectedProductAndVariant.variantTitle,
          image: selectedProductAndVariant.image?.url,
          productTitle: selectedProductAndVariant.productTitle,
          productId: selectedProductAndVariant.productId,
        },
        exchangeGroup: group,
      },
      suggestedExchangeResult,
    );
  };

  const submitProductForGroupExchange = (
    selectedProductAndVariant: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => {
    if (!selectedProductAndVariant) {
      setNext(undefined);
      return;
    }
    compensationForGroupExchange(
      selectedProductAndVariant,
      group,
      suggestedExchangeResult,
    );
  };

  const flow = useMemo(
    () => getReturnFlow(returnItem, draftReturn),
    [draftReturn, returnItem],
  );

  const resetDraftReturn = async (
    client: CustomerPortalRpcClient,
    draftReturn: DraftReturn,
    returnableItemId: string,
    step: number,
    flow: any,
  ) => {
    const { draftReturn: updatedDraftReturn } = await client.changeResetPoint({
      draftReturnId: draftReturn._id,
      reset: { returnableItemId, step, flow: draftReturn.flow },
    });
    returnContext.setDraftReturn(updatedDraftReturn);
    exitFlow();
    navigation.push(RouteName.FLOW, { itemId, itemReturnFlowSteps: [] });
  };

  const onChangeResetPoint = useHandler(async (step: number, flow: any) => {
    if (draftReturn && client && isMerchant) {
      setNext(
        () => () =>
          resetDraftReturn(client, draftReturn, returnItem!.id, step, flow),
      );
    }
  });

  const onResetDraftReturn = useHandler(async (step: number, flow: any) => {
    if (draftReturn && client && isMerchant) {
      await resetDraftReturn(client, draftReturn, returnItem!.id, step, flow);
    }
  });

  const getNextFlowStep = (start: number): readonly number[] => {
    if (!draftReturn || !product || !returnItem || !client) {
      return [];
    }
    const { path } = walkFlow({
      draftReturn,
      flow,
      order,
      product,
      returnableItem: returnItem,
      start,
    });

    return path;
  };

  const resetStep =
    product?.reset?.returnFlow?.type === draftReturn?.flow?.type
      ? product?.reset?.step
      : undefined;
  const flowSteps: readonly ItemReturnFlowStep[] = params.itemReturnFlowSteps
    .length
    ? params.itemReturnFlowSteps
    : [
        {
          type: StepType.FLOW,
          flowPath: getNextFlowStep(resetStep ?? 0),
          inputAnswers: [],
          multipleChoiceAnswers: [],
        },
      ];

  const step = flowSteps.at(-1);
  const flowStepIndex = step?.flowPath.at(-1);
  const flowStep =
    flowStepIndex !== undefined ? flow.steps[flowStepIndex] : undefined;

  const showAllExchangeGroups = useHandler(
    (
      exchangeGroups: ExchangeGroup[],
      suggestedExchangeOptions?: {
        suggestedVariantOptions?: { name: string; values: string[] }[];
        disableExchangeValueAsPriceDifference: boolean;
      },
    ) => {
      if (!step) {
        return;
      }

      const newFlow: ItemReturnFlowStep[] = [
        ...flowSteps,
        {
          type: StepType.EXCHANGE_GROUPS,
          exchangeGroups,
          suggestedExchangeOptions: {
            isSuggestedExchange: true,
            suggestedVariantOptions:
              suggestedExchangeOptions?.suggestedVariantOptions,
            disableExchangeValueAsPriceDifference:
              suggestedExchangeOptions?.disableExchangeValueAsPriceDifference ??
              false,
          },
          flowPath: step.flowPath,
          inputAnswers: step.inputAnswers,
          multipleChoiceAnswers: step.multipleChoiceAnswers,
        },
      ];

      navigation.push(RouteName.FLOW, { itemId, itemReturnFlowSteps: newFlow });
    },
  );

  const suggestedExchangesLoad = useLoad(async () => {
    if (
      flowStep?.type === Step.RETURN &&
      step?.type === StepType.FLOW &&
      isAiExchangeEnabled(flowStep, settings?.returnsAiExchange) &&
      !step.preserveSuggestedExchange &&
      // We only want to show the suggested variant exchange options on the Web currently.
      channel === Channel.WEB &&
      // Skip AI exchange suggestions for merchant-created returns
      !draftReturn?.merchantCreated
    ) {
      // Update exchange groups by product id
      const getExchangeGroupsByProductId_ = async (): Promise<
        ExchangeGroupByProductId | undefined
      > => {
        if (product && settings) {
          const exchangeGroups = getEligibleExchangeGroupsForProduct(
            product,
            settings.exchangeGroups,
          );
          if (!exchangeGroups.length) {
            return;
          }
          try {
            return await getExchangeGroupsByProductId(exchangeGroups, settings);
          } catch (error) {
            console.error("Error getting exchange groups by product id", error);
            return new Map();
          }
        }
        return;
      };

      const aiExchangeRequestParams = {
        flow,
        flowStep,
        step,
        client,
        product,
        settings,
        returnItem,
        draftReturn,
      };

      // Update suggestions
      const [
        suggestedVariantOptions,
        recommendedExchangeProducts,
        exchangeGroupsByProductId,
      ] = await Promise.all([
        getSuggestedVariantExchangeOptions(aiExchangeRequestParams),
        getExchangeRecommendations(aiExchangeRequestParams),
        getExchangeGroupsByProductId_(),
      ]);
      suggestedExchangeOptionsContext.setSuggestedVariantOptions(
        suggestedVariantOptions,
      );
      suggestedExchangeOptionsContext.setExchangeRecommendations(
        recommendedExchangeProducts,
      );
      // Ensure we have a clean context before potentially setting up a new suggestion page.
      suggestedExchangeOptionsContext.setExchangeGroupsByProductId(
        exchangeGroupsByProductId,
      );
      // If we return back to this step (by hitting previous/back), we don't want to re-fetch the suggested exchanges.
      step.preserveSuggestedExchange = true;
    }
  }, [flowStep?.type, step?.type, product, settings]);

  const suggestedVariantOptions =
    suggestedExchangeOptionsContext.suggestedVariantOptions;

  let componentProps: ComponentType | null = null;
  let alternateContinueButtonText: string | undefined = undefined;
  switch (flowStep?.type) {
    case Step.REJECT:
      if (!next) {
        setNext(() => () => exitOutOfApp());
      }
      componentProps = {
        type: ComponentStepType.REJECT,
        props: {
          navigation,
          step: flowStep,
          reject: async () => {
            if (draftReturn && client) {
              await rejectItem(draftReturn, client, {
                item: { ...returnItem!, availableQuantity: 1 },
                flow,
                flowPath: step === undefined ? [] : step.flowPath,
                multipleChoiceAnswers: step?.multipleChoiceAnswers ?? [],
                inputAnswers: step?.inputAnswers ?? [],
              });
            }
          },
          merchantCreatedProps: {
            onChangeResetPoint,
            onResetDraftReturn,
            flow: JSON.parse(JSON.stringify(returnFlowJsonFormat.write(flow))),
          },
        },
      };
      break;
    case Step.BLOCK:
      if (!next) {
        setNext(() => () => navigation.pop());
      }
      componentProps = {
        type: ComponentStepType.BLOCK,
        props: { step: flowStep },
      };
      break;
    case Step.INFORMATION:
      if (!next) {
        setNext(() => () => informationStepNext(flowStep));
      }
      componentProps = {
        type: ComponentStepType.INFORMATION,
        props: { step: flowStep },
      };
      break;
    case Step.INPUT: {
      componentProps = {
        type: ComponentStepType.INPUT,
        props: {
          item: returnItem!,
          navigation,
          step: flowStep,
          submit: handleSubmitInput,
        },
      };
      break;
    }
    case Step.MULTIPLE_CHOICE:
      componentProps = {
        type: ComponentStepType.MULTIPLE_CHOICE,
        props: {
          draftReturnId: draftReturn?._id ?? "",
          item: returnItem!,
          onPress: (option) =>
            setNext(
              () => () => selectMultipleChoice(option, option.sourceIndex),
            ),

          step: flowStep,
          stepIndex: flowStepIndex ?? 0,
        },
      };
      break;
    case Step.RETURN: {
      if (!settings) {
        break;
      }

      const shouldShowRecommendedExchanges =
        suggestedExchangeOptionsContext.exchangeRecommendations.length >=
        Math.min(3, flowStep?.aiProductRecommendationLimit ?? 12);

      // There are different stages in the return step
      switch (step?.type) {
        case StepType.FLOW: {
          if (
            step?.alreadySuggestedExchange ||
            suggestedExchangesLoad.pending ||
            (!suggestedVariantOptions?.shouldSuggest &&
              !shouldShowRecommendedExchanges)
          ) {
            componentProps = {
              type: ComponentStepType.RETURN,
              props: {
                item: returnItem!,
                product: product!,
                settings,
                step: flowStep,
                suggestedExchangeOptionsLoadPending:
                  suggestedExchangesLoad.pending &&
                  !step.alreadySuggestedExchange,
                submit: (data: ReturnStepPageOption) =>
                  setNext(
                    () => () =>
                      handleReturnStepOption(
                        data,
                        !!step.alreadySuggestedExchange,
                      ),
                  ),
                copyResource: getResource({
                  overrides: settings.resourceOverride,
                }),
              },
            };
            break;
          }
          // Fall through to SUGGESTED_EXCHANGE if we have a variant to suggest or exchange recommendations that we haven't shown yet.
        }
        case StepType.SUGGESTED_EXCHANGE: {
          // Skip suggested exchange for merchant-created returns
          if (draftReturn?.merchantCreated) {
            setNextAlt(() => () => continueWithoutSuggestedExchange(false));
            break;
          }
          // We will only ever get here by falling through from the FLOW step above.
          // If we have a variant to suggest, we will show it, if we don't, we will fall through once again to the RECOMMENDED_EXCHANGE step.
          if (suggestedVariantOptions?.shouldSuggest) {
            const nonExchangeReturnOptions = getNonExchangeReturnOptions(
              flowStep,
              product,
              settings,
              returnItem,
              draftReturn?.returnType,
            );
            if (
              (nonExchangeReturnOptions.length === 1 &&
                nonExchangeReturnOptions[0]!.type ===
                  ReturnOptionType.SHOP_OR_RETURN) ||
              shouldShowRecommendedExchanges
            ) {
              alternateContinueButtonText = "No thanks";
            } else {
              // If there are other return options that we haven't shown yet.
              alternateContinueButtonText = "Pick another option";
            }

            const returnedProductVariantData = getReturnedProductVariantData(
              product!,
              settings,
              draftReturn?.returnType,
              draftReturn?.currency,
            );

            const exchangeGroupsAllowed = flowStep.options.some(
              (option) =>
                option.method.type === ReturnOptionMethod.EXCHANGE &&
                option.exchangeTypes.exchangeGroups,
            );
            const exchangeGroups = exchangeGroupsAllowed
              ? getEligibleExchangeGroupsForProduct(
                  product!,
                  settings.exchangeGroups,
                )
              : [];

            const productData: ProductData = {
              productId: product!.productId,
              productTitle: product!.title,
              options: product!.variantData.options,
              variants: product!.variantData.variants.map((variant) =>
                exchangeVariantToVariantData(
                  variant,
                  settings,
                  product!,
                  draftReturn?.returnType,
                  draftReturn?.currency,
                ),
              ),
            };

            componentProps = {
              type: ComponentStepType.SUGGESTED_EXCHANGE,
              props: {
                product: productData,
                returnedProductVariantData,
                settings,
                item: returnItem!,
                submit(variant) {
                  setNextAlt(
                    () => () =>
                      shouldShowRecommendedExchanges
                        ? continueToRecommendedExchange()
                        : continueWithoutSuggestedExchange(true),
                  );
                  if (!variant) {
                    setNext(undefined);
                    return;
                  }
                  setNext(() => () => {
                    const suggestedExchangeResult = getSuggestedExchangeResult({
                      type: SuggestedExchangeEventType.VARIANT_EXCHANGE,
                      variantData: variant,
                      suggestions: suggestedVariantOptions?.suggestedOptions,
                    });

                    const selectedReturnOption =
                      getVariantExchangeReturnOption(flowStep);
                    if (selectedReturnOption?.variantExchangeAddOn) {
                      // Push add-on step instead of finalizing
                      const newFlow: ItemReturnFlowStep[] = [
                        ...flowSteps,
                        {
                          type: StepType.VARIANT_EXCHANGE_ADD_ON,
                          selectedVariant: variant,
                          returnOption: selectedReturnOption,
                          flowPath: step?.flowPath ?? [],
                          inputAnswers: step?.inputAnswers ?? [],
                          multipleChoiceAnswers:
                            step?.multipleChoiceAnswers ?? [],
                          suggestedExchangeResult,
                        },
                      ];
                      navigation.push(RouteName.FLOW, {
                        itemId,
                        itemReturnFlowSteps: newFlow,
                      });
                    } else {
                      void compensationMethod(
                        {
                          variantExchangeItem: {
                            variantId: variant.variantId,
                            productId: variant.productId,
                            productTitle: variant.productTitle,
                            preserveProperties: true,
                            title: variant.variantTitle,
                            image: variant.image?.url,
                          },
                        },
                        suggestedExchangeResult,
                      );
                    }
                  });
                },
                disableExchangeValueAsPriceDifference:
                  flowStep.disableExchangeValueAsPriceDifference ?? false,
                suggestedVariantExchangeOptions: suggestedVariantOptions!,
                suggestedExchangeGroupsProps: exchangeGroups.length
                  ? {
                      groups: exchangeGroups,
                      navigation,
                      selectProduct(
                        selectedProductAndVariant,
                        group,
                        suggestedExchangeResult,
                      ) {
                        setNext(
                          () => () =>
                            submitProductForGroupExchange(
                              selectedProductAndVariant,
                              group,
                              suggestedExchangeResult,
                            ),
                        );
                      },
                      submitProduct: submitProductForGroupExchange,
                      showAllExchangeGroups,
                      exchangeRecommendations:
                        suggestedExchangeOptionsContext.exchangeRecommendations,
                    }
                  : undefined,
              },
            };
            break;
          }
          // Fall through to RECOMMENDED_EXCHANGE if we don't have a variant to suggest and no exchange recommendations have been shown yet.
        }
        case StepType.RECOMMENDED_EXCHANGE: {
          alternateContinueButtonText = "No thanks";
          if (!nextAlt) {
            setNextAlt(
              () => () =>
                continueWithoutSuggestedExchange(
                  suggestedVariantOptions?.shouldSuggest,
                ),
            );
          }

          const returnedProductVariantData = getReturnedProductVariantData(
            product!,
            settings,
            draftReturn?.returnType,
            draftReturn?.currency,
          );
          // Customer wants to exchange for the same item, they weren't suggested any other variants, and their item is out of stock
          const outOfStockSameItemReplacement =
            (!suggestedVariantOptions?.shouldSuggest &&
              suggestedVariantOptions?.sameItemReplacement &&
              !returnedProductVariantData?.isInStock) ??
            false;

          componentProps = {
            type: ComponentStepType.RECOMMENDED_EXCHANGE,
            props: {
              item: returnItem!,
              exchangeRecommendations:
                suggestedExchangeOptionsContext.exchangeRecommendations,
              navigation,
              settings,
              outOfStockSameItemReplacement,
              selectProduct(selectedProductAndVariant, index) {
                setNext(
                  () => () =>
                    submitProductForRecommendedExchange(
                      selectedProductAndVariant,
                      index,
                    ),
                );
              },
              submitProduct: submitProductForRecommendedExchange,
              groupsByProductId:
                suggestedExchangeOptionsContext.exchangeGroupsByProductId,
              aiProductRecommendationLimit:
                flowStep?.aiProductRecommendationLimit,
              suggestedExchangeOptions: {
                suggestedVariantOptions:
                  suggestedVariantOptions?.suggestedOptions,
                disableExchangeValueAsPriceDifference:
                  flowStep.disableExchangeValueAsPriceDifference ?? false,
              },
            },
          };
          break;
        }
        case StepType.VARIANT_EXCHANGE: {
          let returnedProductVariantData: VariantData | undefined;
          const originalVariant = product!.variantData.variants.find(
            (variant) => variant.id === product?.variantId,
          );
          if (originalVariant) {
            returnedProductVariantData = exchangeVariantToVariantData(
              originalVariant,
              settings,
              product!,
              draftReturn?.returnType,
              draftReturn?.currency,
            );
          }
          componentProps = {
            type: ComponentStepType.VARIANT_EXCHANGE,
            props: {
              product: {
                productId: product!.productId,
                productTitle: product!.title,
                options: product!.variantData.options,
                variants: product!.variantData.variants.map((variant) =>
                  exchangeVariantToVariantData(
                    variant,
                    settings,
                    product!,
                    draftReturn?.returnType,
                    draftReturn?.currency,
                  ),
                ),
              },
              returnedProductVariantData,
              submit(variant) {
                if (!variant) {
                  setNext(undefined);
                  return;
                }
                setNext(() => () => {
                  const selectedReturnOption =
                    getVariantExchangeReturnOption(flowStep);

                  if (selectedReturnOption?.variantExchangeAddOn) {
                    // Push add-on step instead of finalizing
                    const newFlow: ItemReturnFlowStep[] = [
                      ...flowSteps,
                      {
                        type: StepType.VARIANT_EXCHANGE_ADD_ON,
                        selectedVariant: variant,
                        returnOption: selectedReturnOption,
                        flowPath: step?.flowPath ?? [],
                        inputAnswers: step?.inputAnswers ?? [],
                        multipleChoiceAnswers:
                          step?.multipleChoiceAnswers ?? [],
                      },
                    ];
                    navigation.push(RouteName.FLOW, {
                      itemId,
                      itemReturnFlowSteps: newFlow,
                    });
                  } else {
                    // Normal flow - finalize immediately
                    void compensationMethod({
                      variantExchangeItem: {
                        variantId: variant.variantId,
                        productId: variant.productId,
                        productTitle: variant.productTitle,
                        preserveProperties: true,
                        title: variant.variantTitle,
                        image: variant.image?.url,
                      },
                    });
                  }
                });
              },
              suggestedVariantExchangeOptions: undefined,
              disableExchangeValueAsPriceDifference: false,
              settings,
              item: returnItem!,
            },
          };
          break;
        }
        case StepType.EXCHANGE_GROUPS: {
          componentProps = {
            type: ComponentStepType.EXCHANGE_GROUPS,
            props: {
              item: returnItem!,
              groups: getEligibleExchangeGroupsForProduct(
                product!,
                settings.exchangeGroups,
              ),
              navigation,
              selectProduct(
                selectedProductAndVariant,
                group,
                suggestedExchangeResult,
              ) {
                setNext(
                  () => () =>
                    submitProductForGroupExchange(
                      selectedProductAndVariant,
                      group,
                      suggestedExchangeResult,
                    ),
                );
              },
              submitProduct: submitProductForGroupExchange,
              suggestedExchangeOptions: step.suggestedExchangeOptions
                ?.isSuggestedExchange
                ? {
                    shouldFireEvent: true,
                    fromSuggestedPreview: false,
                    groupsByProductId:
                      suggestedExchangeOptionsContext.exchangeGroupsByProductId,
                    originalProduct: {
                      productId: product!.productId,
                      productTitle: product!.title,
                      options: product!.variantData.options,
                      variants: product!.variantData.variants.map((variant) =>
                        exchangeVariantToVariantData(
                          variant,
                          settings,
                          product!,
                          draftReturn?.returnType,
                          draftReturn?.currency,
                        ),
                      ),
                    },
                    suggestedVariantOptions:
                      step.suggestedExchangeOptions?.suggestedVariantOptions,
                    disableExchangeValueAsPriceDifference:
                      step.suggestedExchangeOptions
                        ?.disableExchangeValueAsPriceDifference ?? false,
                  }
                : undefined,
            },
          };
          break;
        }
        case StepType.VARIANT_EXCHANGE_ADD_ON: {
          componentProps = {
            type: ComponentStepType.VARIANT_EXCHANGE_ADD_ON,
            props: getVariantExchangeAddOnProps(
              step.selectedVariant,
              step.returnOption,
              step.returnOption.variantExchangeAddOnVariantId,
            ),
          };
          break;
        }
      }
      break;
    }
    case Step.SUBMIT_WARRANTY:
    case Step.SUBMIT_REPAIR:
      componentProps = { type: ComponentStepType.FIXED_COMPENSATION };
      if (!error) {
        submitFixedCompensation(flowStep);
      }
      break;
    case Step.SUBMIT_CLAIM:
      if (
        flowStep.option.method.type === ReturnOptionMethod.EXCHANGE &&
        flowStep.option.variantExchangeAddOn &&
        settings &&
        product
      ) {
        const sameItemVariant = getSameItemVariant(
          product,
          settings,
          draftReturn?.returnType,
        );
        if (sameItemVariant) {
          const selectedVariant = exchangeVariantToVariantData(
            sameItemVariant,
            settings,
            product,
            draftReturn?.returnType,
            draftReturn?.currency,
          );

          componentProps = {
            type: ComponentStepType.VARIANT_EXCHANGE_ADD_ON,
            props: getVariantExchangeAddOnProps(
              selectedVariant,
              flowStep.option,
              flowStep.option.variantExchangeAddOnVariantId,
            ),
          };
          break;
        }
      }

      componentProps = { type: ComponentStepType.FIXED_COMPENSATION };
      submitFixedCompensation(flowStep);
      break;
    default:
      throw new Error(
        `Invalid return flow step type: ${flowStep?.type.toString()}`,
      );
  }
  const isRejected = componentProps?.type === ComponentStepType.REJECT;

  return {
    texts: {
      continueButton: isSubmittingFlow
        ? "Loading..."
        : componentProps?.type === ComponentStepType.INFORMATION
          ? componentProps.props.step.continueButtonText
          : "Continue",
      continueWithoutButton: alternateContinueButtonText,
    },
    componentProps: componentProps!,
    error:
      !returnItem || !draftReturn || !product ? "Return item not found" : error,
    next,
    nextButtonDisabled: !next || isSubmittingFlow,
    nextAlt,
    nextAltButtonDisabled: !nextAlt || isSubmittingFlow,
    previousButtonDisabled: isSubmittingFlow || (isRejected && !isMerchant),
    isRejected,
    itemDetailParams: returnItem
      ? returnItemToParams({
          item: returnItem,
          returnType: draftReturn?.returnType,
          presentmentCurrency: draftReturn?.presentmentCurrency
            ? String(draftReturn.presentmentCurrency)
            : null,
          exchangeRate: draftReturn?.exchangeRate ?? 1,
        })
      : undefined,
  };
};
